/**
 * Pure cadence helper for recurring_invoice_series.
 *
 * Used by:
 *   - Operator UI (preview the next 1–3 dates a series will fire)
 *   - The Piece-2 sweep handler in process-scheduled-tasks (inlined there
 *     too — Supabase Edge Functions can't import from src/lib; if you
 *     touch this file, touch the inline copy in the function).
 *
 * Pure: no DB, no env, no Date.now() dependencies. Reproducible, Deno-safe.
 *
 * ─── Frequency semantics ───────────────────────────────────────────────────
 *   weekly    → current + 7 days
 *   monthly   → current + 1 month, with end-of-month CLAMPING
 *               (Jan 31 → Feb 28 in non-leap; Jan 31 2024 → Feb 29; Mar 31 → Apr 30)
 *   quarterly → current + 3 months, same clamping
 *   yearly    → current + 12 months, same clamping (only matters for Feb 29
 *               in a leap year → Feb 28 in the next non-leap year)
 *
 * v1 deliberately ignores daysOfWeek for weekly. The column is reserved on
 * the table for a future enhancement ("every Monday and Thursday"). For now,
 * weekly is always +7 days from the current run date.
 *
 * ─── Clamping detail (the only non-trivial bit) ────────────────────────────
 * JS Date overflows month-day silently: new Date(Date.UTC(2026, 1, 31))
 * yields 2026-03-03 (Feb 31 overflows into March). We CLAMP instead — find
 * the last day of the target month and use min(currentDay, lastDayOfTarget).
 * This matches what every reference billing system (Stripe, QuickBooks,
 * Jobber) does for "monthly on the 31st" recurring schedules.
 */

export type Frequency = 'weekly' | 'monthly' | 'quarterly' | 'yearly'

/**
 * Compute the next run date for a recurring schedule.
 *
 * @param current YYYY-MM-DD ISO date string (no time component)
 * @param frequency One of weekly/monthly/quarterly/yearly
 * @param _daysOfWeek Reserved for future weekly-by-weekday support; ignored in v1
 * @returns YYYY-MM-DD ISO date string
 */
export function computeNextRunDate(
  current: string,
  frequency: Frequency,
  _daysOfWeek?: string[] | null,
): string {
  const parts = current.split('-').map(Number)
  if (parts.length !== 3 || parts.some(n => !Number.isFinite(n))) {
    throw new Error(`computeNextRunDate: invalid date string ${current}`)
  }
  const [y, m, d] = parts

  if (frequency === 'weekly') {
    // +7 days. UTC math avoids local-timezone DST shifts at the day boundary.
    const next = new Date(Date.UTC(y, m - 1, d + 7))
    return formatISODate(next)
  }

  const monthsToAdd =
    frequency === 'monthly'   ? 1  :
    frequency === 'quarterly' ? 3  :
    /* yearly */               12

  // Compute target year + month (0-indexed)
  const totalMonths = (m - 1) + monthsToAdd
  const targetYear = y + Math.floor(totalMonths / 12)
  const targetMonth0 = ((totalMonths % 12) + 12) % 12  // safe modulo for any sign

  // End-of-month CLAMP: find last day of target month, take min(d, lastDay).
  // Date.UTC(year, monthIndex+1, 0) → last day of monthIndex (overflow trick).
  const lastDayOfTarget = new Date(Date.UTC(targetYear, targetMonth0 + 1, 0)).getUTCDate()
  const clampedDay = Math.min(d, lastDayOfTarget)

  return formatISODate(new Date(Date.UTC(targetYear, targetMonth0, clampedDay)))
}

function formatISODate(d: Date): string {
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
